import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getUserSessions } from '../../lib/session'
import { getSessionMessages, getSessionArtifacts, getSessionParticipants, getSessionReflections } from '../../lib/organization'
import { downloadSessionBundle } from '../../lib/download'
import { signOut, signInWithGoogle } from '../../lib/auth'
import { useAuth } from '../auth/AuthProvider'
import { useToast } from '../common/Toast'
import { Copy, Check, ArrowRight, Download, LogOut, ArrowLeft } from 'lucide-react'
import type { SessionMetadata } from '../../types'
import { STAGE_NAMES, AGENT_DISPLAY_NAMES } from '../../types'

interface SessionWithId extends SessionMetadata {
  id: string
}

/** A solo session has no lobby to gather in, so resuming means the session itself. */
function resumeHref(session: SessionWithId): string {
  return session.isSolo ? `/session/${session.id}` : `/lobby/${session.id}`
}

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  completed: { label: 'Done', className: 'border-teal text-teal' },
  active: { label: 'Active', className: 'border-amber text-amber-dark' },
  lobby: { label: 'In lobby', className: 'border-navy/55 text-navy/70' },
}

export default function MySessionsPage() {
  const navigate = useNavigate()
  // Sessions are listed by uid rather than by account, so a joiner who was given an
  // anonymous identity still finds the team rooms they were part of.
  const { user, uid, loading: authLoading } = useAuth()
  const { addToast } = useToast()
  const [sessions, setSessions] = useState<SessionWithId[]>([])
  const [loading, setLoading] = useState(true)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  useEffect(() => {
    if (authLoading) return
    if (!uid) {
      setLoading(false)
      return
    }
    setLoading(true)
    getUserSessions(uid)
      .then(setSessions)
      .catch((err) => console.error('getUserSessions failed:', err))
      .finally(() => setLoading(false))
  }, [uid, authLoading])

  function copyRoomCode(sessionId: string, code: string) {
    navigator.clipboard.writeText(code)
    setCopiedId(sessionId)
    setTimeout(() => setCopiedId(null), 2000)
  }

  async function handleDownload(session: SessionWithId) {
    setDownloadingId(session.id)
    try {
      const [messages, artifacts, participants, reflections] = await Promise.all([
        getSessionMessages(session.id),
        getSessionArtifacts(session.id),
        getSessionParticipants(session.id),
        getSessionReflections(session.id),
      ])
      await downloadSessionBundle(session.sessionName, messages, artifacts, participants, reflections)
    } catch (err) {
      console.error('Bundle download failed:', err)
      addToast('The download did not finish. Try again.', 'error')
    } finally {
      setDownloadingId(null)
    }
  }

  const activeSessions = sessions.filter(s => s.status !== 'completed')
  const completedSessions = sessions.filter(s => s.status === 'completed')

  return (
    <div className="min-h-screen bg-stone font-body text-navy">
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-navy text-stone px-6 py-3 flex items-center justify-between border-b-2 border-golden">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:text-stone/80 transition cursor-pointer">
            <ArrowLeft size={16} />
            <span className="hl text-xl text-stone">Day One</span>
          </button>
          <span className="mono-label text-golden border border-golden/60 px-1.5 py-0.5">
            Beta
          </span>
        </div>
        <div className="flex items-center gap-4">
          {user && (
            <span className="mono-label text-stone/70 hidden sm:inline truncate max-w-[16rem]">
              {user.displayName || user.email}
            </span>
          )}
          {user ? (
            <button
              onClick={() => { signOut(); navigate('/') }}
              className="mono-label text-stone/70 hover:text-stone transition flex items-center gap-1.5 cursor-pointer"
            >
              <LogOut size={14} />
              Sign out
            </button>
          ) : (
            <button
              onClick={() => signInWithGoogle()}
              className="mono-label text-stone/75 hover:text-stone transition cursor-pointer"
            >
              Sign in
            </button>
          )}
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <h1 className="hl text-3xl md:text-4xl text-navy border-b-2 border-navy pb-3 mb-8">My sessions</h1>

        {!uid && !authLoading && (
          <div className="text-center py-16">
            <p className="descriptor text-navy/70 mb-5">Sign in to see your sessions.</p>
            <button onClick={() => signInWithGoogle()} className="btn btn-amber">
              Sign in with Google
            </button>
          </div>
        )}

        {(authLoading || (uid && loading)) && (
          <div className="text-center py-16">
            <p className="mono-label text-navy/70">
              Loading your sessions
              <span className="caret" aria-hidden="true" />
            </p>
          </div>
        )}

        {uid && !loading && !authLoading && sessions.length === 0 && (
          <div className="text-center py-16">
            <p className="descriptor text-navy/70 mb-5">Nothing here yet. Your first session takes about 25 minutes.</p>
            <button onClick={() => navigate('/solo')} className="btn btn-amber">
              Start your first session
            </button>
          </div>
        )}

        {activeSessions.length > 0 && (
          <>
            <h2 className="mono-label text-teal border-b-2 border-navy pb-2 mb-4">In progress</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-10">
              {activeSessions.map(session => {
                const status = STATUS_STYLES[session.status] || STATUS_STYLES.lobby
                return (
                  <div
                    key={session.id}
                    className="card card-shadow card-lift p-5"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-display text-base text-navy leading-tight line-clamp-2">
                        {session.sessionName}
                      </h3>
                      <span className={`mono-label border px-1.5 py-0.5 shrink-0 ml-2 ${status.className}`}>
                        {status.label}
                      </span>
                    </div>

                    <div className="mono-label text-navy/70 space-y-1 mb-4 normal-case tracking-[0.08em]">
                      <p>Stage {session.currentStage}: {STAGE_NAMES[session.currentStage]} ({AGENT_DISPLAY_NAMES[session.currentAgent]})</p>
                      <p>{session.teamSize} participant{session.teamSize !== 1 ? 's' : ''}</p>
                      {session.createdAt?.toDate && (
                        <p>{session.createdAt.toDate().toLocaleDateString()}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {!session.isSolo && (
                        <button
                          onClick={() => copyRoomCode(session.id, session.roomCode)}
                          className="flex items-center gap-1.5 font-mono text-xs tracking-[0.2em] bg-paper border border-navy/55 px-2.5 py-1.5 text-navy/75 hover:border-navy hover:text-navy transition cursor-pointer"
                        >
                          {session.roomCode}
                          {copiedId === session.id ? <Check size={12} className="text-teal" /> : <Copy size={12} />}
                        </button>
                      )}
                      <a
                        href={resumeHref(session)}
                        onClick={(e) => { e.preventDefault(); navigate(resumeHref(session)) }}
                        className="ml-auto flex items-center gap-1 mono-label text-amber hover:text-amber-dark transition"
                      >
                        Resume <ArrowRight size={14} />
                      </a>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}

        {completedSessions.length > 0 && (
          <>
            <h2 className="mono-label text-teal border-b-2 border-navy pb-2 mb-4">Completed</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {completedSessions.map(session => (
                <div
                  key={session.id}
                  className="card card-shadow card-lift p-5"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-display text-base text-navy leading-tight line-clamp-2">
                      {session.sessionName}
                    </h3>
                    <span className="mono-label border border-teal text-teal px-1.5 py-0.5 shrink-0 ml-2">
                      Done
                    </span>
                  </div>

                  <div className="mono-label text-navy/70 space-y-1 mb-4 normal-case tracking-[0.08em]">
                    <p>{session.teamSize} participant{session.teamSize !== 1 ? 's' : ''}</p>
                    {session.createdAt?.toDate && (
                      <p>{session.createdAt.toDate().toLocaleDateString()}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleDownload(session)}
                      disabled={downloadingId === session.id}
                      className="flex items-center gap-1.5 mono-label text-navy border-2 border-navy px-3 py-2 hover:bg-paper transition disabled:opacity-50 cursor-pointer"
                    >
                      <Download size={13} />
                      {downloadingId === session.id ? 'Downloading' : 'Download'}
                    </button>
                    {!session.isSolo && (
                      <button
                        onClick={() => copyRoomCode(session.id, session.roomCode)}
                        className="ml-auto flex items-center gap-1.5 font-mono text-xs tracking-[0.2em] bg-paper border border-navy/55 px-2.5 py-1.5 text-navy/75 hover:border-navy hover:text-navy transition cursor-pointer"
                      >
                        {session.roomCode}
                        {copiedId === session.id ? <Check size={12} className="text-teal" /> : <Copy size={12} />}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
